import React, { useEffect, useState } from 'react'
import api from '../api/client'
import type { VolunteerMetadata } from '../types'
import { Users, Mail, Phone, Search, MessageCircle, Edit3, Award, ChevronLeft, ChevronRight, UserPlus, UserMinus, Trash2, Shield, User, Calendar, Download, KeyRound, MessageSquare } from 'lucide-react'
import { useOpenConversation } from '../hooks/useOpenConversation'
import { useAuth } from '../contexts/AuthContext'
import EditVolunteerSkillsModal from '../components/EditVolunteerSkillsModal'
import VolunteerShiftsModal from '../components/VolunteerShiftsModal'
import InvitePanel from '../components/InvitePanel'
import { useLocale } from '../contexts/LocaleContext'
import { Tag } from '@rallyshift/design-system'
import { isOrganiserRole, roleLabel } from '../utils/status'
import * as XLSX from 'xlsx'
import { format } from 'date-fns'
import { formatDayDateToLocal } from '../utils/timezone'

const WINTER_EVENT_SKILLS = [
  'School schaatsbegeleiding',
  'Kassa / schaatsverhuur',
  'Toezichthouder',
  'Passe-partouts controle',
  'Op- afbouw',
  'Technische ondersteuning',
  'BHV',
  'Social media content creatie',
  'Fotograaf',
  'Video producent'
]

const ITEMS_PER_PAGE = 50

interface UserWithRole extends VolunteerMetadata {
  profile_role?: string
  is_volunteer?: boolean
  shift_count?: number
}

interface EditProfileModalProps {
  isOpen: boolean
  onClose: () => void
  user: UserWithRole
  onProfileUpdated: () => void
}

const EditProfileModal: React.FC<EditProfileModalProps> = ({ isOpen, onClose, user, onProfileUpdated }) => {
  const [firstName, setFirstName] = useState(user.first_name || '')
  const [lastName, setLastName] = useState(user.last_name || '')
  const [email, setEmail] = useState(user.email || '')
  const [phoneNumber, setPhoneNumber] = useState(user.phone_number || '')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (isOpen) {
      setFirstName(user.first_name || '')
      setLastName(user.last_name || '')
      setEmail(user.email || '')
      setPhoneNumber(user.phone_number || '')
      setError('')
    }
  }, [isOpen, user])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!firstName.trim() || !lastName.trim() || !email.trim()) {
      setError('First name, last name, and email are required')
      return
    }

    try {
      setLoading(true)
      setError('')

      // Update the profile
      await api.profiles.update(user.id, {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        email: email.trim(),
        phone_number: phoneNumber.trim() || undefined
      })

      // Update volunteer metadata if it exists
      try {
        await api.volunteerMetadata.upsert(user.id, {
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          email: email.trim(),
          phone_number: phoneNumber.trim() || undefined
        })
      } catch (metadataErr) {
        console.warn('Failed to update volunteer metadata:', metadataErr)
      }

      onProfileUpdated()
    } catch (err: any) {
      console.error('Error updating profile:', err)
      setError(err.message || 'Failed to update profile')
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">
              Edit Profile: {user.first_name} {user.last_name}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
                First Name *
              </label>
              <input
                type="text"
                id="firstName"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                required
              />
            </div>

            <div>
              <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
                Last Name *
              </label>
              <input
                type="text"
                id="lastName"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                required
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email *
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                required
              />
            </div>

            <div>
              <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700">
                Phone Number
              </label>
              <input
                type="tel"
                id="phoneNumber"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="Optional"
              />
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {loading ? 'Updating...' : 'Update Profile'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

const VolunteersPage: React.FC = () => {
  const { isVolunteer, isAdmin, user } = useAuth()
  const { t } = useLocale()
  const { open: openChat, opening } = useOpenConversation()
  const [volunteers, setVolunteers] = useState<UserWithRole[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [manualResetLink, setManualResetLink] = useState('')
  const [manualResetLinks, setManualResetLinks] = useState<{ email: string; reset_url: string }[]>([])
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [batchBusy, setBatchBusy] = useState(false)
  const [isSkillsModalOpen, setIsSkillsModalOpen] = useState(false)
  const [selectedVolunteer, setSelectedVolunteer] = useState<VolunteerMetadata | null>(null)
  const [skillsFilter, setSkillsFilter] = useState<string>('all')
  const [currentPage, setCurrentPage] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [showAllUsers, setShowAllUsers] = useState(false)
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false)
  const [selectedUserForEdit, setSelectedUserForEdit] = useState<UserWithRole | null>(null)
  const [isShiftsModalOpen, setIsShiftsModalOpen] = useState(false)
  const [selectedVolunteerForShifts, setSelectedVolunteerForShifts] = useState<UserWithRole | null>(null)
  const [exportLoading, setExportLoading] = useState(false)
  const [isCreateVolunteerModalOpen, setIsCreateVolunteerModalOpen] = useState(false)

  useEffect(() => {
    if (isVolunteer || isAdmin) {
      fetchVolunteers()
    } else {
      setError('You need to be a volunteer or admin to view this page')
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVolunteer, isAdmin, currentPage, debouncedSearchTerm, skillsFilter, showAllUsers])

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm)
      setCurrentPage(1) // Reset to first page when searching
    }, 300)

    return () => clearTimeout(timer)
  }, [searchTerm])

  // Reset to first page when skills filter changes
  useEffect(() => {
    setCurrentPage(1)
  }, [skillsFilter])

  useEffect(() => {
    setSelectedIds([])
  }, [currentPage, debouncedSearchTerm, skillsFilter, showAllUsers])

  const fetchShiftCounts = async (): Promise<Map<string, number>> => {
    try {
      const counts = await api.shifts.getAssignmentCounts()
      const map = new Map<string, number>()
      Object.entries(counts || {}).forEach(([id, n]) => map.set(id, n))
      return map
    } catch (err) {
      console.error('Error fetching shift counts:', err)
      return new Map()
    }
  }

  const fetchVolunteers = async () => {
    try {
      setLoading(true)
      setError('')

      let usersData: any[] = []
      if (isAdmin && showAllUsers) {
        const profiles = await api.profiles.list()
        usersData = (profiles || []).map((profile: any) => ({
          id: profile.id,
          first_name: profile.first_name || '',
          last_name: profile.last_name || '',
          email: profile.email,
          avatar_url: profile.avatar_url,
          phone_number: profile.phone_number,
          role: profile.role,
          skills: profile.skills,
          created_at: profile.created_at,
          updated_at: profile.updated_at,
          profile_role: profile.role,
          is_volunteer: profile.role === 'volunteer' || profile.role === 'admin',
          shift_count: 0
        }))
      } else {
        const volunteers = await api.volunteerMetadata.list()
        usersData = (volunteers || []).map((v: any) => ({
          ...v,
          shift_count: 0
        }))
      }

      const shiftCountMap = await fetchShiftCounts()
      usersData = usersData.map(user => ({
        ...user,
        shift_count: shiftCountMap.get(user.id) || 0
      }))

      const term = debouncedSearchTerm.trim().toLowerCase()
      if (term) {
        usersData = usersData.filter(u =>
          (u.first_name || '').toLowerCase().includes(term) ||
          (u.last_name || '').toLowerCase().includes(term) ||
          (u.email || '').toLowerCase().includes(term)
        )
      }

      if (skillsFilter !== 'all') {
        usersData = usersData.filter(u => u.skills && u.skills.includes(skillsFilter))
      }

      const count = usersData.length
      const totalPages = Math.ceil(count / ITEMS_PER_PAGE) || 1
      const offset = (currentPage - 1) * ITEMS_PER_PAGE
      const pageData = usersData.slice(offset, offset + ITEMS_PER_PAGE)

      setVolunteers(pageData)
      setTotalCount(count)
      setTotalPages(totalPages)
    } catch (err: any) {
      console.error('Error in fetchVolunteers:', err)
      setError(err.message || 'Failed to fetch volunteers')
    } finally {
      setLoading(false)
    }
  }

  const handleEditSkills = (volunteer: VolunteerMetadata) => {
    setSelectedVolunteer(volunteer)
    setIsSkillsModalOpen(true)
  }

  const handleSkillsUpdated = () => {
    fetchVolunteers() // Refresh the volunteers list
  }

  const handleEditProfile = (user: UserWithRole) => {
    setSelectedUserForEdit(user)
    setIsProfileModalOpen(true)
  }

  const handleProfileUpdated = () => {
    fetchVolunteers() // Refresh the volunteers list
    setIsProfileModalOpen(false)
    setSelectedUserForEdit(null)
  }

  const handleViewShifts = (volunteer: UserWithRole) => {
    setSelectedVolunteerForShifts(volunteer)
    setIsShiftsModalOpen(true)
  }

  const handleShiftsModalClose = () => {
    setIsShiftsModalOpen(false)
    setSelectedVolunteerForShifts(null)
  }

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1)
    }
  }

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1)
    }
  }

  const handleUpgradeToVolunteer = async (userId: string) => {
    try {
      setActionLoading(userId)
      setError('')
      const profile = await api.profiles.get(userId)
      await api.profiles.update(userId, { role: 'volunteer' })
      try {
        await api.volunteerMetadata.upsert(userId, {
          first_name: profile.first_name || '',
          last_name: profile.last_name || '',
          email: profile.email,
          role: 'volunteer'
        })
      } catch (metadataErr) {
        console.warn('Failed to update volunteer metadata:', metadataErr)
      }
      await fetchVolunteers()
    } catch (err: any) {
      console.error('Error upgrading user to volunteer:', err)
      setError(err.message || 'Failed to upgrade user to volunteer')
    } finally {
      setActionLoading(null)
    }
  }

  const handleDowngradeToUser = async (userId: string) => {
    try {
      setActionLoading(userId)
      setError('')
      await api.profiles.update(userId, { role: 'user' })
      try {
        await api.volunteerMetadata.delete(userId)
      } catch (deleteErr) {
        console.warn('Failed to remove from volunteer metadata:', deleteErr)
      }
      await fetchVolunteers()
    } catch (err: any) {
      console.error('Error downgrading volunteer to user:', err)
      setError(err.message || 'Failed to downgrade volunteer to user')
    } finally {
      setActionLoading(null)
    }
  }

  const handleUpgradeToAdmin = async (userId: string) => {
    if (!window.confirm('Are you sure you want to upgrade this user to admin? This will give them full system access.')) {
      return
    }
    try {
      setActionLoading(userId)
      setError('')
      const profile = await api.profiles.get(userId)
      await api.profiles.update(userId, { role: 'admin' })
      try {
        await api.volunteerMetadata.upsert(userId, {
          first_name: profile.first_name || '',
          last_name: profile.last_name || '',
          email: profile.email,
          role: 'admin'
        })
      } catch (metadataErr) {
        console.warn('Failed to update volunteer metadata:', metadataErr)
      }
      await fetchVolunteers()
    } catch (err: any) {
      console.error('Error upgrading user to admin:', err)
      setError(err.message || 'Failed to upgrade user to admin')
    } finally {
      setActionLoading(null)
    }
  }

  const handleRemoveUser = async (volunteer: UserWithRole) => {
    const name = `${volunteer.first_name} ${volunteer.last_name}`.trim() || volunteer.email
    if (!window.confirm(t('volunteers.removeVolunteerConfirm').replace('{name}', name))) {
      return
    }
    try {
      setActionLoading(volunteer.id)
      setError('')
      setSuccess('')
      await api.profiles.delete(volunteer.id)
      await fetchVolunteers()
    } catch (err: any) {
      console.error('Error removing user:', err)
      setError(err.message || 'Failed to remove user')
    } finally {
      setActionLoading(null)
    }
  }

  const handleSendPasswordReset = async (volunteer: UserWithRole) => {
    try {
      setActionLoading(volunteer.id)
      setError('')
      setSuccess('')
      setManualResetLink('')
      setManualResetLinks([])
      const result = await api.auth.sendPasswordReset(volunteer.id)
      setSuccess(t('volunteers.passwordResetSent').replace('{email}', volunteer.email))
      setManualResetLinks([])
      if (result.reset_url) setManualResetLink(result.reset_url)
    } catch (err: any) {
      console.error('Error sending password reset:', err)
      setError(err.message || t('volunteers.sendPasswordReset'))
    } finally {
      setActionLoading(null)
    }
  }

  const canResetPassword = (volunteer: UserWithRole) =>
    isAdmin && !!user?.id && volunteer.id !== user.id

  const canRemoveVolunteer = (volunteer: UserWithRole) =>
    canResetPassword(volunteer) && volunteer.role !== 'admin' && volunteer.role !== 'owner'

  const toggleSelected = (id: string) => {
    setSelectedIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]))
  }

  const selectableOnPage = volunteers.filter((v) => canResetPassword(v))
  const allOnPageSelected =
    selectableOnPage.length > 0 && selectableOnPage.every((v) => selectedIds.includes(v.id))

  const toggleSelectAllOnPage = () => {
    if (allOnPageSelected) {
      const pageIds = new Set(selectableOnPage.map((v) => v.id))
      setSelectedIds((ids) => ids.filter((id) => !pageIds.has(id)))
      return
    }
    setSelectedIds((ids) => [
      ...ids,
      ...selectableOnPage.map((v) => v.id).filter((id) => !ids.includes(id)),
    ])
  }

  const selectedOnPage = volunteers.filter((v) => selectedIds.includes(v.id))
  const selectedForReset = selectedOnPage.filter((v) => canResetPassword(v))
  const selectedForRemove = selectedOnPage.filter((v) => canRemoveVolunteer(v))

  const handleBatchPasswordReset = async () => {
    if (!selectedForReset.length) return
    if (
      !window.confirm(
        t('volunteers.batchResetConfirm').replace('{count}', String(selectedForReset.length)),
      )
    ) {
      return
    }
    try {
      setBatchBusy(true)
      setError('')
      setSuccess('')
      setManualResetLink('')
      setManualResetLinks([])
      const result = await api.auth.sendPasswordResetBatch(selectedForReset.map((v) => v.id))
      setSuccess(
        t('volunteers.batchResetDone').replace('{sent}', String(result.sent)),
      )
      if (result.reset_urls?.length) setManualResetLinks(result.reset_urls)
      setSelectedIds([])
    } catch (err: any) {
      setError(err.message || t('volunteers.batchReset'))
    } finally {
      setBatchBusy(false)
    }
  }

  const handleBatchRemove = async () => {
    if (!selectedForRemove.length) return
    if (
      !window.confirm(
        t('volunteers.batchRemoveConfirm').replace('{count}', String(selectedForRemove.length)),
      )
    ) {
      return
    }
    try {
      setBatchBusy(true)
      setError('')
      setSuccess('')
      const result = await api.profiles.removeBatch(selectedForRemove.map((v) => v.id))
      setSuccess(t('volunteers.batchRemoveDone').replace('{removed}', String(result.removed)))
      setSelectedIds([])
      await fetchVolunteers()
    } catch (err: any) {
      setError(err.message || t('volunteers.batchRemove'))
    } finally {
      setBatchBusy(false)
    }
  }

  const exportVolunteersToExcel = async () => {
    if (!isAdmin) {
      setError(t('volunteers.exportError'))
      return
    }

    setExportLoading(true)
    setError('')

    try {
      // Fetch all volunteers with their shift counts
      let volunteersData: any[] = []
      if (showAllUsers) {
        volunteersData = await api.profiles.list() || []
      } else {
        volunteersData = await api.volunteerMetadata.list() || []
      }
      const shiftCounts = await fetchShiftCounts()

      // Prepare data for Excel export
      const exportData = volunteersData.map(volunteer => {
        const shiftCount = shiftCounts.get(volunteer.id) || 0
        const skills = Array.isArray(volunteer.skills) ? volunteer.skills.join('; ') : (volunteer.skills || '')
        
        return {
          'First Name': volunteer.first_name || '',
          'Last Name': volunteer.last_name || '',
          'Email': volunteer.email || '',
          'Phone Number': volunteer.phone_number || '',
          'Role': volunteer.role || 'user',
          'Skills': skills,
          'Shift Count': shiftCount,
          'Created At': formatDayDateToLocal(volunteer.created_at, 'dd/MM/yyyy HH:mm'),
          'Updated At': formatDayDateToLocal(volunteer.updated_at, 'dd/MM/yyyy HH:mm')
        }
      })

      // Create workbook and worksheet
      const wb = XLSX.utils.book_new()
      const ws = XLSX.utils.json_to_sheet(exportData)

      // Auto-size columns
      const colWidths = [
        { wch: 15 }, // First Name
        { wch: 15 }, // Last Name
        { wch: 25 }, // Email
        { wch: 15 }, // Phone Number
        { wch: 12 }, // Role
        { wch: 40 }, // Skills
        { wch: 12 }, // Shift Count
        { wch: 18 }, // Created At
        { wch: 18 }  // Updated At
      ]
      ws['!cols'] = colWidths

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, 'Volunteers')

      // Generate filename with current date
      const now = new Date()
      const dateStr = format(now, 'yyyy-MM-dd_HH-mm')
      const filename = `volunteers_export_${dateStr}.xlsx`

      // Save file
      XLSX.writeFile(wb, filename)

    } catch (err: any) {
      console.error('Error exporting volunteers:', err)
      setError(err.message || t('volunteers.exportError'))
    } finally {
      setExportLoading(false)
    }
  }

  const CreateVolunteerModal: React.FC<{ isOpen: boolean; onClose: () => void }> = ({ isOpen, onClose }) => {
    const [formData, setFormData] = useState({
      email: '',
      password: '',
      confirmPassword: '',
      firstName: '',
      lastName: '',
      phoneNumber: '',
      skills: [] as string[]
    })
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')

    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault()
      setLoading(true)
      setError('')

      // Validation
      if (formData.password !== formData.confirmPassword) {
        setError('Passwords do not match')
        setLoading(false)
        return
      }

      if (formData.password.length < 6) {
        setError('Password must be at least 6 characters')
        setLoading(false)
        return
      }

      if (!formData.email || !formData.firstName || !formData.lastName) {
        setError('Please fill in all required fields')
        setLoading(false)
        return
      }

      try {
        await api.auth.createVolunteer({
          email: formData.email,
          password: formData.password,
          firstName: formData.firstName,
          lastName: formData.lastName,
          phoneNumber: formData.phoneNumber || undefined,
          skills: formData.skills
        })
        await fetchVolunteers()
        onClose()
        setFormData({
          email: '',
          password: '',
          confirmPassword: '',
          firstName: '',
          lastName: '',
          phoneNumber: '',
          skills: []
        })
      } catch (err: any) {
        console.error('Error creating volunteer:', err)
        setError(err.message || 'Failed to create volunteer')
      } finally {
        setLoading(false)
      }
    }

    const handleSkillToggle = (skill: string) => {
      setFormData(prev => ({
        ...prev,
        skills: prev.skills.includes(skill)
          ? prev.skills.filter(s => s !== skill)
          : [...prev.skills, skill]
      }))
    }

    if (!isOpen) return null

    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-medium text-gray-900">
                {t('volunteers.createVolunteer')}
              </h3>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Basic Information */}
              <div className="space-y-4">
                <h4 className="text-sm font-medium text-gray-900 border-b border-gray-200 pb-2">
                  Basic Information
                </h4>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('volunteers.firstName')} *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.firstName}
                      onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('volunteers.lastName')} *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.lastName}
                      onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('volunteers.email')} *
                  </label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('volunteers.phone')}
                  </label>
                  <input
                    type="tel"
                    value={formData.phoneNumber}
                    onChange={(e) => setFormData(prev => ({ ...prev, phoneNumber: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              {/* Account Security */}
              <div className="space-y-4">
                <h4 className="text-sm font-medium text-gray-900 border-b border-gray-200 pb-2">
                  Account Security
                </h4>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Password *
                  </label>
                  <input
                    type="password"
                    required
                    value={formData.password}
                    onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Minimum 6 characters"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Confirm Password *
                  </label>
                  <input
                    type="password"
                    required
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              {/* Skills */}
              <div className="space-y-4">
                <h4 className="text-sm font-medium text-gray-900 border-b border-gray-200 pb-2">
                  Skills & Expertise
                </h4>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    {t('volunteers.selectSkills')} (Optional)
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-40 overflow-y-auto border border-gray-200 rounded-md p-3">
                    {WINTER_EVENT_SKILLS.map((skill) => (
                      <label key={skill} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-1 rounded">
                        <input
                          type="checkbox"
                          checked={formData.skills.includes(skill)}
                          onChange={() => handleSkillToggle(skill)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">{skill}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? (
                    <span className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Creating...
                    </span>
                  ) : (
                    t('volunteers.createVolunteer')
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-gray-900">
            {isAdmin && showAllUsers ? 'Alle Gebruikers' : 'Vrijwilligers'}
          </h1>
          <p className="mt-2 text-sm text-gray-700">
            {isAdmin && showAllUsers 
              ? 'Een lijst van alle gebruikers in het systeem. Beheerders kunnen gebruikers upgraden naar vrijwilligers of verwijderen.'
              : 'Een lijst van alle vrijwilligers. Je kunt iemand verwijderen of een resetlink voor hun wachtwoord sturen.'
            }
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
          <div className="flex items-center space-x-4">
            {isAdmin && (
              <button
                onClick={() => setShowAllUsers(!showAllUsers)}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                {showAllUsers ? 'Toon Alleen Vrijwilligers' : 'Toon Alle Gebruikers'}
              </button>
            )}
            {isAdmin && (
              <button
                onClick={() => setIsCreateVolunteerModalOpen(true)}
                className="inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm leading-4 font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              >
                <UserPlus className="h-4 w-4 mr-2" />
                {t('volunteers.addVolunteer')}
              </button>
            )}
            {isAdmin && (
              <button
                onClick={exportVolunteersToExcel}
                disabled={exportLoading}
                className="inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {exportLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {t('volunteers.exporting')}
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4 mr-2" />
                    {t('volunteers.export')}
                  </>
                )}
              </button>
            )}
            <div className="text-sm text-gray-500">
              Totaal: {volunteers.length} {isAdmin && showAllUsers ? 'gebruikers' : 'vrijwilligers'}
            </div>
          </div>
        </div>
      </div>

      {isAdmin && <InvitePanel onChanged={fetchVolunteers} />}

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        {/* Search */}
        <div className="relative flex-1">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            placeholder={t('volunteers.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {/* Skills Filter */}
        <div className="sm:w-64">
          <select
            value={skillsFilter}
            onChange={(e) => setSkillsFilter(e.target.value)}
            className="block w-full px-3 py-2 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
          >
            <option value="all">{t('volunteers.filterAllSkills')}</option>
            {WINTER_EVENT_SKILLS.map(skill => (
              <option key={skill} value={skill}>{skill}</option>
            ))}
          </select>
        </div>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <div className="text-sm text-red-700">{error}</div>
        </div>
      )}
      {success && (
        <div className="rounded-md bg-green-50 p-4">
          <div className="text-sm text-green-800">{success}</div>
          {manualResetLink && (
            <p className="mt-2 break-all text-sm text-green-900">
              {t('volunteers.passwordResetManual')} {manualResetLink}
            </p>
          )}
          {manualResetLinks.length > 0 && (
            <ul className="mt-2 space-y-1 text-sm text-green-900">
              {manualResetLinks.map((row) => (
                <li key={row.email} className="break-all">
                  {row.email}: {row.reset_url}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {isAdmin && selectedIds.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 rounded-md border border-gray-200 bg-gray-50 px-4 py-3">
          <span className="text-sm font-medium text-gray-700">
            {t('volunteers.selectedCount').replace('{count}', String(selectedIds.length))}
          </span>
          <button
            type="button"
            onClick={handleBatchPasswordReset}
            disabled={batchBusy || selectedForReset.length === 0}
            className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-amber-800 bg-amber-50 border border-amber-200 rounded-md hover:bg-amber-100 disabled:opacity-50"
          >
            <KeyRound className="h-4 w-4 mr-1.5" />
            {t('volunteers.batchReset')}
          </button>
          <button
            type="button"
            onClick={handleBatchRemove}
            disabled={batchBusy || selectedForRemove.length === 0}
            className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 disabled:opacity-50"
          >
            <Trash2 className="h-4 w-4 mr-1.5" />
            {t('volunteers.batchRemove')}
          </button>
        </div>
      )}

      {/* Volunteers list */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-sm text-gray-500">Laden...</p>
          </div>
        ) : volunteers.length === 0 ? (
          <div className="text-center py-12">
            <Users className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">{t('volunteers.noVolunteers')}</h3>
            <p className="mt-1 text-sm text-gray-500">
              {debouncedSearchTerm ? 'Probeer uw zoektermen aan te passen.' : 'Er zijn nog geen vrijwilligers toegevoegd.'}
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-gray-200">
            {isAdmin && selectableOnPage.length > 0 && (
              <li className="bg-gray-50 px-4 py-2">
                <label className="inline-flex items-center gap-2 text-sm text-gray-600">
                  <input
                    type="checkbox"
                    checked={allOnPageSelected}
                    onChange={toggleSelectAllOnPage}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  {t('volunteers.selectAll')}
                </label>
              </li>
            )}
            {volunteers.map((volunteer) => (
              <li key={volunteer.id}>
                <div className="px-4 py-4">
                  <div className="flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
                    <div className="flex-shrink-0 flex items-center space-x-3 sm:space-x-4">
                      {isAdmin && canResetPassword(volunteer) && (
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(volunteer.id)}
                          onChange={() => toggleSelected(volunteer.id)}
                          aria-label={`${volunteer.first_name} ${volunteer.last_name}`}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      )}
                      {volunteer.avatar_url ? (
                        <img
                          className="h-12 w-12 sm:h-10 sm:w-10 rounded-full"
                          src={volunteer.avatar_url}
                          alt={`${volunteer.first_name} ${volunteer.last_name}`}
                        />
                      ) : (
                        <div className="h-12 w-12 sm:h-10 sm:w-10 rounded-full bg-gray-300 flex items-center justify-center">
                          <span className="text-sm font-medium text-gray-700">
                            {volunteer.first_name[0]}{volunteer.last_name[0]}
                          </span>
                        </div>
                      )}
                      <div className="sm:hidden">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {volunteer.first_name} {volunteer.last_name}
                        </p>
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
                        <div className="sm:hidden w-full">
                          <div className="flex items-center justify-start space-x-3 mt-2">
                            <a 
                              href={`mailto:${volunteer.email}`}
                              className="inline-flex items-center justify-center w-12 h-12 bg-blue-100 hover:bg-blue-200 rounded-full transition-colors"
                              title={`Email ${volunteer.email}`}
                            >
                              <Mail className="h-6 w-6 text-blue-600" />
                            </a>
                            {volunteer.phone_number && (
                              <>
                                <a 
                                  href={`tel:${volunteer.phone_number}`}
                                  className="inline-flex items-center justify-center w-12 h-12 bg-green-100 hover:bg-green-200 rounded-full transition-colors"
                                  title={`Call ${volunteer.phone_number}`}
                                >
                                  <Phone className="h-6 w-6 text-green-600" />
                                </a>
                                <a 
                                  href={`https://wa.me/${volunteer.phone_number.replace(/[^\d]/g, '')}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center justify-center w-12 h-12 bg-green-500 hover:bg-green-600 rounded-full transition-colors"
                                  title={`WhatsApp ${volunteer.phone_number}`}
                                >
                                  <MessageCircle className="h-6 w-6 text-white" />
                                </a>
                              </>
                            )}
                          </div>
                          {/* Mobile admin controls */}
                          {isAdmin && (
                            <div className="flex flex-wrap items-center gap-2 mt-3">
                              <Tag selected={isOrganiserRole(volunteer.role)}>
                                {roleLabel(volunteer.role)}
                              </Tag>
                              {/* Shift count and view button for mobile - show for volunteers */}
                              {(volunteer.role === 'volunteer' || volunteer.role === 'admin') && (
                                <>
                                  <Tag>
                                    <Calendar className="h-3 w-3" />
                                    {volunteer.shift_count || 0} diensten
                                  </Tag>
                                  <button
                                    onClick={() => handleViewShifts(volunteer)}
                                    className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 transition-colors"
                                    title="View shifts"
                                  >
                                    <Calendar className="h-3 w-3 mr-1" />
                                    View Shifts
                                  </button>
                                </>
                              )}
                              <button
                                onClick={() => handleEditProfile(volunteer)}
                                className="inline-flex items-center px-2 py-1 text-xs font-medium text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-md hover:bg-indigo-100 transition-colors"
                                title="Edit profile"
                              >
                                <User className="h-3 w-3 mr-1" />
                                Profile
                              </button>
                              {volunteer.role === 'volunteer' && (
                                <button
                                  onClick={() => handleEditSkills(volunteer)}
                                  className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 transition-colors"
                                  title="Edit skills"
                                >
                                  <Edit3 className="h-3 w-3 mr-1" />
                                  Skills
                                </button>
                              )}
                              {showAllUsers && (
                                <>
                                  {volunteer.role === 'user' && (
                                    <button
                                      onClick={() => handleUpgradeToVolunteer(volunteer.id)}
                                      disabled={actionLoading === volunteer.id}
                                      className="inline-flex items-center px-2 py-1 text-xs font-medium text-green-600 bg-green-50 border border-green-200 rounded-md hover:bg-green-100 transition-colors disabled:opacity-50"
                                      title="Upgrade to volunteer"
                                    >
                                      {actionLoading === volunteer.id ? (
                                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-green-600 mr-1"></div>
                                      ) : (
                                        <UserPlus className="h-3 w-3 mr-1" />
                                      )}
                                      Upgrade
                                    </button>
                                  )}
                                  {volunteer.role === 'volunteer' && (
                                    <>
                                      <button
                                        onClick={() => handleUpgradeToAdmin(volunteer.id)}
                                        disabled={actionLoading === volunteer.id}
                                        className="inline-flex items-center px-2 py-1 text-xs font-medium text-purple-600 bg-purple-50 border border-purple-200 rounded-md hover:bg-purple-100 transition-colors disabled:opacity-50"
                                        title="Upgrade to admin"
                                      >
                                        {actionLoading === volunteer.id ? (
                                          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-purple-600 mr-1"></div>
                                        ) : (
                                          <Shield className="h-3 w-3 mr-1" />
                                        )}
                                        Make Admin
                                      </button>
                                      <button
                                        onClick={() => handleDowngradeToUser(volunteer.id)}
                                        disabled={actionLoading === volunteer.id}
                                        className="inline-flex items-center px-2 py-1 text-xs font-medium text-orange-600 bg-orange-50 border border-orange-200 rounded-md hover:bg-orange-100 transition-colors disabled:opacity-50"
                                        title="Downgrade to user"
                                      >
                                        {actionLoading === volunteer.id ? (
                                          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-orange-600 mr-1"></div>
                                        ) : (
                                          <UserMinus className="h-3 w-3 mr-1" />
                                        )}
                                        Downgrade
                                      </button>
                                    </>
                                  )}
                                  {volunteer.role !== 'admin' && (
                                    <button
                                      onClick={() => handleRemoveUser(volunteer)}
                                      disabled={actionLoading === volunteer.id}
                                      className="inline-flex items-center px-2 py-1 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 transition-colors disabled:opacity-50"
                                      title="Remove user"
                                    >
                                      {actionLoading === volunteer.id ? (
                                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-red-600 mr-1"></div>
                                      ) : (
                                        <Trash2 className="h-3 w-3 mr-1" />
                                      )}
                                      Remove
                                    </button>
                                  )}
                                </>
                              )}
                              {volunteer.id !== user?.id && (
                                <button
                                  onClick={() => openChat(() => api.messaging.openDirect(volunteer.id))}
                                  disabled={opening}
                                  className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50"
                                  title={t('messages.message')}
                                >
                                  <MessageSquare className="h-3 w-3 mr-1" />
                                  {t('messages.message')}
                                </button>
                              )}
                              {canResetPassword(volunteer) && (
                                <button
                                  onClick={() => handleSendPasswordReset(volunteer)}
                                  disabled={actionLoading === volunteer.id}
                                  className="inline-flex items-center px-2 py-1 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-md hover:bg-amber-100 transition-colors disabled:opacity-50"
                                  title={t('volunteers.sendPasswordReset')}
                                >
                                  {actionLoading === volunteer.id ? (
                                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-amber-700 mr-1"></div>
                                  ) : (
                                    <KeyRound className="h-3 w-3 mr-1" />
                                  )}
                                  {t('volunteers.sendPasswordReset')}
                                </button>
                              )}
                              {canRemoveVolunteer(volunteer) && !showAllUsers && (
                                <button
                                  onClick={() => handleRemoveUser(volunteer)}
                                  disabled={actionLoading === volunteer.id}
                                  className="inline-flex items-center px-2 py-1 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 transition-colors disabled:opacity-50"
                                  title={t('volunteers.removeVolunteer')}
                                >
                                  {actionLoading === volunteer.id ? (
                                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-red-600 mr-1"></div>
                                  ) : (
                                    <Trash2 className="h-3 w-3 mr-1" />
                                  )}
                                  {t('volunteers.removeVolunteer')}
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="hidden sm:block">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {volunteer.first_name} {volunteer.last_name}
                          </p>
                          <div className="flex flex-col sm:flex-row sm:items-center space-y-1 sm:space-y-0 sm:space-x-4 mt-1">
                            <div className="flex items-center space-x-2">
                              <a 
                                href={`mailto:${volunteer.email}`}
                                className="inline-flex items-center justify-center w-8 h-8 bg-blue-100 hover:bg-blue-200 rounded-full transition-colors"
                                title={`Email ${volunteer.email}`}
                              >
                                <Mail className="h-4 w-4 text-blue-600" />
                              </a>
                              <a 
                                href={`mailto:${volunteer.email}`}
                                className="text-sm text-gray-500 hover:text-blue-600 transition-colors truncate"
                              >
                                {volunteer.email}
                              </a>
                            </div>
                            {volunteer.phone_number && (
                              <div className="flex items-center space-x-2">
                                <a 
                                  href={`tel:${volunteer.phone_number}`}
                                  className="inline-flex items-center justify-center w-8 h-8 bg-green-100 hover:bg-green-200 rounded-full transition-colors"
                                  title={`Call ${volunteer.phone_number}`}
                                >
                                  <Phone className="h-4 w-4 text-green-600" />
                                </a>
                                <a 
                                  href={`tel:${volunteer.phone_number}`}
                                  className="text-sm text-gray-500 hover:text-blue-600 transition-colors"
                                >
                                  {volunteer.phone_number}
                                </a>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="hidden sm:flex flex-shrink-0 flex-row items-center justify-end space-x-2">
                          <div className="flex flex-wrap items-center gap-2">
                            <Tag selected={isOrganiserRole(volunteer.role)}>
                              {roleLabel(volunteer.role)}
                            </Tag>
                            {/* Shift count and view button - show for volunteers */}
                            {(volunteer.role === 'volunteer' || volunteer.role === 'admin') && (
                              <div className="flex items-center space-x-1">
                                <Tag>
                                  <Calendar className="h-3 w-3" />
                                  {volunteer.shift_count || 0} diensten
                                </Tag>
                                <button
                                  onClick={() => handleViewShifts(volunteer)}
                                  className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 transition-colors"
                                  title="View shifts"
                                >
                                  <Calendar className="h-3 w-3 mr-1" />
                                  View
                                </button>
                              </div>
                            )}
                            {isAdmin && (
                              <>
                                <button
                                  onClick={() => handleEditProfile(volunteer)}
                                  className="inline-flex items-center px-2 py-1 text-xs font-medium text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-md hover:bg-indigo-100 transition-colors"
                                  title="Edit profile"
                                >
                                  <User className="h-3 w-3 mr-1" />
                                  Profile
                                </button>
                                {volunteer.role === 'volunteer' && (
                                  <button
                                    onClick={() => handleEditSkills(volunteer)}
                                    className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 transition-colors"
                                    title="Edit skills"
                                  >
                                    <Edit3 className="h-3 w-3 mr-1" />
                                    Skills
                                  </button>
                                )}
                                {showAllUsers && (
                                  <>
                                    {volunteer.role === 'user' && (
                                      <button
                                        onClick={() => handleUpgradeToVolunteer(volunteer.id)}
                                        disabled={actionLoading === volunteer.id}
                                        className="inline-flex items-center px-2 py-1 text-xs font-medium text-green-600 bg-green-50 border border-green-200 rounded-md hover:bg-green-100 transition-colors disabled:opacity-50"
                                        title="Upgrade to volunteer"
                                      >
                                        {actionLoading === volunteer.id ? (
                                          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-green-600 mr-1"></div>
                                        ) : (
                                          <UserPlus className="h-3 w-3 mr-1" />
                                        )}
                                        Upgrade
                                      </button>
                                    )}
                                    {volunteer.role === 'volunteer' && (
                                      <>
                                        <button
                                          onClick={() => handleUpgradeToAdmin(volunteer.id)}
                                          disabled={actionLoading === volunteer.id}
                                          className="inline-flex items-center px-2 py-1 text-xs font-medium text-purple-600 bg-purple-50 border border-purple-200 rounded-md hover:bg-purple-100 transition-colors disabled:opacity-50"
                                          title="Upgrade to admin"
                                        >
                                          {actionLoading === volunteer.id ? (
                                            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-purple-600 mr-1"></div>
                                          ) : (
                                            <Shield className="h-3 w-3 mr-1" />
                                          )}
                                          Make Admin
                                        </button>
                                        <button
                                          onClick={() => handleDowngradeToUser(volunteer.id)}
                                          disabled={actionLoading === volunteer.id}
                                          className="inline-flex items-center px-2 py-1 text-xs font-medium text-orange-600 bg-orange-50 border border-orange-200 rounded-md hover:bg-orange-100 transition-colors disabled:opacity-50"
                                          title="Downgrade to user"
                                        >
                                          {actionLoading === volunteer.id ? (
                                            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-orange-600 mr-1"></div>
                                          ) : (
                                            <UserMinus className="h-3 w-3 mr-1" />
                                          )}
                                          Downgrade
                                        </button>
                                      </>
                                    )}
                                    {volunteer.role !== 'admin' && (
                                      <button
                                        onClick={() => handleRemoveUser(volunteer)}
                                        disabled={actionLoading === volunteer.id}
                                        className="inline-flex items-center px-2 py-1 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 transition-colors disabled:opacity-50"
                                        title="Remove user"
                                      >
                                        {actionLoading === volunteer.id ? (
                                          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-red-600 mr-1"></div>
                                        ) : (
                                          <Trash2 className="h-3 w-3 mr-1" />
                                        )}
                                        Remove
                                      </button>
                                    )}
                                  </>
                                )}
                                {volunteer.id !== user?.id && (
                                  <button
                                    onClick={() => openChat(() => api.messaging.openDirect(volunteer.id))}
                                    disabled={opening}
                                    className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50"
                                    title={t('messages.message')}
                                  >
                                    <MessageSquare className="h-3 w-3 mr-1" />
                                    {t('messages.message')}
                                  </button>
                                )}
                                {canResetPassword(volunteer) && (
                                  <button
                                    onClick={() => handleSendPasswordReset(volunteer)}
                                    disabled={actionLoading === volunteer.id}
                                    className="inline-flex items-center px-2 py-1 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-md hover:bg-amber-100 transition-colors disabled:opacity-50"
                                    title={t('volunteers.sendPasswordReset')}
                                  >
                                    {actionLoading === volunteer.id ? (
                                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-amber-700 mr-1"></div>
                                    ) : (
                                      <KeyRound className="h-3 w-3 mr-1" />
                                    )}
                                    {t('volunteers.sendPasswordReset')}
                                  </button>
                                )}
                                {canRemoveVolunteer(volunteer) && !showAllUsers && (
                                  <button
                                    onClick={() => handleRemoveUser(volunteer)}
                                    disabled={actionLoading === volunteer.id}
                                    className="inline-flex items-center px-2 py-1 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 transition-colors disabled:opacity-50"
                                    title={t('volunteers.removeVolunteer')}
                                  >
                                    {actionLoading === volunteer.id ? (
                                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-red-600 mr-1"></div>
                                    ) : (
                                      <Trash2 className="h-3 w-3 mr-1" />
                                    )}
                                    {t('volunteers.removeVolunteer')}
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {/* Skills display */}
                      {volunteer.skills && volunteer.skills.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-gray-100">
                          <div className="flex items-center mb-2">
                            <Award className="h-4 w-4 text-gray-400 mr-1" />
                            <span className="text-xs font-medium text-gray-500">Skills:</span>
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {volunteer.skills.map((skill, index) => (
                              <Tag key={index}>{skill}</Tag>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Pagination */}
      {!loading && volunteers.length > 0 && totalPages > 1 && (
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={handlePreviousPage}
              disabled={currentPage === 1}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Vorige
            </button>
            <button
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Volgende
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Toon <span className="font-medium">{((currentPage - 1) * ITEMS_PER_PAGE) + 1}</span> tot{' '}
                <span className="font-medium">
                  {Math.min(currentPage * ITEMS_PER_PAGE, totalCount)}
                </span>{' '}
                van <span className="font-medium">{totalCount}</span> resultaten
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button
                  onClick={handlePreviousPage}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">Vorige</span>
                  <ChevronLeft className="h-5 w-5" aria-hidden="true" />
                </button>
                
                {/* Page numbers */}
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum: number
                  if (totalPages <= 5) {
                    pageNum = i + 1
                  } else if (currentPage <= 3) {
                    pageNum = i + 1
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i
                  } else {
                    pageNum = currentPage - 2 + i
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        currentPage === pageNum
                          ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  )
                })}
                
                <button
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">Volgende</span>
                  <ChevronRight className="h-5 w-5" aria-hidden="true" />
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Skills Edit Modal */}
      <EditVolunteerSkillsModal
        isOpen={isSkillsModalOpen}
        onClose={() => {
          setIsSkillsModalOpen(false)
          setSelectedVolunteer(null)
        }}
        volunteer={selectedVolunteer}
        onSkillsUpdated={handleSkillsUpdated}
      />

      {/* Profile Edit Modal */}
      {isProfileModalOpen && selectedUserForEdit && (
        <EditProfileModal
          isOpen={isProfileModalOpen}
          onClose={() => {
            setIsProfileModalOpen(false)
            setSelectedUserForEdit(null)
          }}
          user={selectedUserForEdit}
          onProfileUpdated={handleProfileUpdated}
        />
      )}

      {/* Volunteer Shifts Modal */}
      <VolunteerShiftsModal
        isOpen={isShiftsModalOpen}
        onClose={handleShiftsModalClose}
        volunteer={selectedVolunteerForShifts}
      />

      {/* Create Volunteer Modal */}
      <CreateVolunteerModal
        isOpen={isCreateVolunteerModalOpen}
        onClose={() => setIsCreateVolunteerModalOpen(false)}
      />
    </div>
  )
}

export default VolunteersPage
